import { Tween } from "./index.types";
import { AnimationAction, LoopOnce, LoopPingPong, LoopRepeat } from "three";

// /*
//   Async wrapper for Three.js AnimationAction that makes it awaitable and simplifies looping:
//   @see https://threejs.org/docs/#AnimationAction
//   - `play()` returns a promise for use with `await` or `.then()`.
//   - Default behavior is to play once instead of looping infinitely.
//   - Infinite looping must be explicitly enabled via `.repeat(-1)`.
//   - Supports delays between repeats. 
// */

export class THREETween implements Tween {
    private action: AnimationAction;
    private callbacks: { onStart?: () => void; onUpdate?: () => void; onComplete?: () => void; } = {};
    private hooked = false;
    public id?: string;
    private delaySeconds: number | null; 

    constructor(action: AnimationAction, id?: string) {
        this.action = action
        this.action.paused = true;
        this.id = id ? id : crypto.randomUUID();
        this.delaySeconds = null; 
        this.callbacks = {}
        this.hookMixerUpdate()
    }

    duration() {
        return this.action.getClip().duration;
    }

    play() {
    const play = () => 
    {
        this.action.paused = false;
        this.action.setLoop(LoopOnce, 1)
        this.action.play();
    }
    if (!this.delaySeconds) play()
    if (this.delaySeconds) setTimeout(() => play(), this.delaySeconds * 1000)
    return this

    }

    delay(v?: number) {
        if (v !== undefined) this.delaySeconds = v; 
        return v !== undefined ? v : this.delaySeconds
    }

    invalidate() {
        this.reset()
    }

    pause() {
        this.action.paused = true;
        return this; 
    }

    resume() {
        this.action.paused = false;
        this.action.play();
        return this; 
    }

    playPause(time?: number) {
        this.action.play();
        if (time !== undefined) 
        {
            this.action.time = time;
            this.action.getMixer().update(0);
        }
        this.action.paused = true;
        return this; 
    }

    revert() {
        this.callbacks = {}; 
        this.action.reset();
        this.pause()
        return this; 
    }

    reset() {
        this.action.reset()
        this.pause()
        return this;
    }

    kill() {
        this.callbacks = {}; 
        this.action.stop();
        this.pause()
        return this; 
    }

    progress(value?: number) {
    if (value === undefined) return this.action.time / this.duration();
    
        const d = this.duration();
        if (!d) return 0;
        const progress = value * d; 
        // Ensure the action is active so its pose is applied
        this.action.enabled = true;
        this.action.setEffectiveWeight(1);
        this.action.play();
        this.action.paused = true;
        this.action.time = progress;
        // Apply absolute time without advancing
        this.action.getMixer().update(0);
        return progress;
    }

    repeat(n?: number) {
        if (n === undefined) return;
        this.action.setLoop(n === -1 ? LoopRepeat : LoopPingPong, n === -1 ? Infinity : n);
        return this
    }

    private hookMixerUpdate() {
        if (this.hooked) return;
        this.hooked = true;

        const mixer = this.action.getMixer();
        const originalUpdate = mixer.update.bind(mixer);

        mixer.update = (dt: number) => {
            const result = originalUpdate(dt);
            this.tick();
            return result;
        };
    }

    // Callbacks
    eventCallback(type: "onStart" | "onUpdate" | "onComplete", callback: () => void) {
        this.callbacks[type] = callback;

        // Remove the clearing logic - let tick() handle cleanup
        const mixer = this.action.getMixer();
        const onFinished = (e: any) => {
        if (e.action !== this.action) return;
        mixer.removeEventListener("finished", onFinished);
        // Don't clear this.callbacks here
        };
        mixer.addEventListener("finished", onFinished);
    }

    tick() {
        if (this.action.paused || !this.duration()) return;
        const p = this.progress();

        if (this.callbacks.onStart && p > 0) {
            this.callbacks.onStart();
            this.callbacks.onStart = undefined;
        }

        if (this.callbacks.onUpdate && p > 0 && p < 1) {
            this.callbacks.onUpdate();
            //this.callbacks.onUpdate = undefined; // gets cleaned up by the listener
        }

        if (this.callbacks.onComplete && p >= 1) {
            this.callbacks.onComplete();
            this.callbacks.onComplete = undefined;
        }
    }

    then(
        onFulfilled?: ((value: Omit<this, "then">) => any) | null,
        _onRejected?: ((reason: any) => any) | null
    ) {
        const mixer = this.action.getMixer();
        return new Promise<this>((resolve) => {
            const onFinished = (e: any) => {
                if (e.action !== this.action) return;
                mixer.removeEventListener("finished", onFinished);
                onFulfilled?.(this);
                resolve(this);
            };
            mixer.addEventListener("finished", onFinished);
        });
    }
}
